import { useCallback } from "react";
import useSWR from "swr";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";

// Een stabiele lege lijst. Zou dit `[]` zijn, dan krijgt elke render een nieuwe referentie,
// en een effect dat `products` in zijn dep-array heeft draait dan eindeloos rond zolang de
// producten nog laden of de read is mislukt. Zelfde reden als NO_LINKS in useProductLinks.
const NO_PRODUCTS = [];

const SHOPS = [
  { shopSource: "VCE", firestore: () => vceFirestore },
  { shopSource: "HVO", firestore: () => hvoFirestore },
];

/**
 * Fetcher for products - both databases, each on its own.
 *
 * One shop failing (typically HVO: its session can be missing, see loginToBothFirebase) used to
 * blank the whole list, because both reads sat in one promise. A pick round built from an empty
 * catalogue treats every package as a plain article, so the shops are read independently and
 * the result says which ones actually loaded.
 */
const fetchProductsFromFirestore = async () => {
  const results = await Promise.all(
    SHOPS.map(async ({ shopSource, firestore }) => {
      try {
        const snapshot = await firestore().collection("products").get();
        return { shopSource, products: snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data(), shopSource })) };
      } catch (error) {
        console.error(`[useProducts] ${shopSource} products could not be read:`, error);
        return { shopSource, products: [], error };
      }
    }),
  );
  return {
    products: results.flatMap((result) => result.products),
    loadedShops: results.filter((result) => !result.error).map((result) => result.shopSource),
    errorsByShop: results.reduce((errors, result) => (result.error ? { ...errors, [result.shopSource]: result.error } : errors), {}),
  };
};

const NO_SHOPS = [];
const NO_ERRORS = {};

/**
 * Hook to fetch and manage products from both VCE and HVO Firestore databases with SWR caching
 * @param {Object} options - Configuration options
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 0 - no auto refresh)
 * @returns {Object} - Products data and utility functions
 */
export const useProducts = (options = {}) => {
  const { refreshInterval = 0 } = options;

  // Use SWR for data fetching with caching
  const { data, error: fetchError, isLoading, mutate } = useSWR(
    "products",
    fetchProductsFromFirestore,
    {
      refreshInterval,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 10000, // Dedupe requests within 10 seconds
    }
  );

  const products = data ? data.products : undefined;
  const errorsByShop = data ? data.errorsByShop : NO_ERRORS;
  // Keep `error` meaning "something did not load", as the screens already show it
  const error = fetchError || Object.values(errorsByShop)[0] || undefined;

  /**
   * Manually refresh products
   * @returns {Promise<Array>} - Updated products
   */
  const refreshProducts = useCallback(() => {
    return mutate();
  }, [mutate]);

  /**
   * Get products filtered by shop source
   * @param {string} shopSource - "VCE" or "HVO"
   * @returns {Array} - Filtered products
   */
  const getProductsByShop = useCallback(
    (shopSource) => {
      if (!shopSource || !products) return products || [];
      return products.filter((product) => product.shopSource === shopSource);
    },
    [products],
  );

  /**
   * Find a specific product by ID
   * @param {string} productId - The product ID to search for
   * @returns {Object|null} - The product object or null if not found
   */
  const findProductById = useCallback(
    (productId) => {
      if (!productId || !products) return null;
      return products.find((product) => product.id === productId) || null;
    },
    [products],
  );

  /**
   * Get products that have productItems (combo products)
   * @returns {Array} - Products with productItems
   */
  const getComboProducts = useCallback(() => {
    if (!products) return [];
    return products.filter((product) => product.productItems && product.productItems.length > 0);
  }, [products]);

  /**
   * Get frozen products
   * @returns {Array} - Frozen products
   */
  const getFrozenProducts = useCallback(() => {
    if (!products) return [];
    return products.filter((product) => product.frozen === true);
  }, [products]);

  return {
    products: products || NO_PRODUCTS,
    isLoading,
    isLoaded: !!data,
    error,
    /** Which shops' catalogues actually loaded - a shop that failed is absent, not empty. */
    loadedShops: data ? data.loadedShops : NO_SHOPS,
    errorsByShop,
    refreshProducts,
    getProductsByShop,
    findProductById,
    getComboProducts,
    getFrozenProducts,
  };
};
